import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    LayoutDashboard, Users, Loader2, MessageSquare, Building2, ShieldCheck, Star, CheckCircle2,
} from "lucide-react";
import { DashboardLayout, StatCard } from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/** Auth-gated shell for /team/* (Hiring Team Member role). */
const TeamLayout = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login"); return; }
        if (user.role !== "hiring_team") {
            navigate(user.role === "employer" ? "/employer/dashboard" : user.role === "professional" ? "/professional/dashboard" : "/");
        }
    }, [user, loading, navigate]);

    if (loading || !user) {
        return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 size={22} className="animate-spin text-emerald-500" /></div>;
    }

    const sidebar = [
        {
            section: "Review",
            items: [
                { to: "/team/dashboard", exact: true, icon: LayoutDashboard, label: "Dashboard", testId: "tsidebar-dashboard" },
                { to: "/team/candidates", icon: Users, label: "Candidates", testId: "tsidebar-candidates" },
                { to: "/inbox", icon: MessageSquare, label: "Inbox", testId: "tsidebar-inbox" },
            ],
        },
    ];
    return <DashboardLayout sidebar={sidebar} />;
};

export const TeamDashboard = () => {
    const [data, setData] = useState(null);
    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/team/dashboard");
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="team-dashboard-page">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <p className="overline">Hiring team member</p>
                <h1 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">
                    Welcome to {data.membership?.company_name || data.membership?.employer_name}&apos;s hiring room <span className="inline-block">👋</span>
                </h1>
                <p className="mt-2 text-muted-foreground max-w-2xl">
                    Review candidates, leave comments, and vote on finalists. Your feedback shows up alongside every teammate&apos;s.
                </p>
                <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <ShieldCheck size={14} className="text-brand-green" />
                    <span>Scoped access · You can&apos;t see billing, job settings, or team invites.</span>
                </div>
            </motion.div>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testId="tstat-roles" icon={Building2} label="Open Roles" value={data.active_roles} sub="Active AI Hiring Briefs" accent="green" />
                <StatCard testId="tstat-candidates" to="/team/candidates" icon={Users} label="In Pipeline" value={data.candidates_in_pipeline} sub="Across all roles" accent="blue" />
                <StatCard testId="tstat-interviews" icon={Star} label="Interviewing" value={data.stage_counts?.interview || 0} sub={`${data.stage_counts?.offer || 0} at offer stage`} accent="amber" />
                <StatCard testId="tstat-hired" icon={CheckCircle2} label="Hired" value={data.stage_counts?.hired || 0} sub="Successful placements" accent="violet" />
            </div>

            <div className="mt-10 card-soft p-6">
                <h2 className="font-heading font-semibold text-lg text-foreground">Your role, at a glance</h2>
                <ul className="mt-4 space-y-2">
                    <li className="flex items-start gap-2 text-sm text-foreground"><CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" /> Review every candidate in the pipeline with match reasons + gaps.</li>
                    <li className="flex items-start gap-2 text-sm text-foreground"><CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" /> Leave threaded comments that the whole hiring team sees.</li>
                    <li className="flex items-start gap-2 text-sm text-foreground"><CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" /> Star-rate finalists 1–5. Averages roll up automatically.</li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground"><Star size={15} className="text-muted-foreground mt-0.5 shrink-0" /> Video review + interview scheduling — coming soon.</li>
                </ul>
            </div>
        </div>
    );
};

export default TeamLayout;
